import React, {useCallback, useContext, useMemo} from "react";
import {DataGridMiscContext, DataGridStateContext} from "../../../index";
import classnames from "classnames";
import {DataGridColumnPinnedTypes, DataGridExoticColumnFields, DefaultDataGridCell} from "../../../type-declerations";
import DataGridBodyRow from "./row";
import DataGridUtils from "../../../core/services/utils";

/**
 * @param {DataGridRow[]} rows
 * @param {number | undefined} layoutRectWidth
 * @param {Function} startScrolling
 * @return {JSX.Element}
 * @constructor
 */
const DataGridBody = ({
                          rows,
                          layoutRectWidth,
                          startScrolling,
                      }) => {
    const {selectedRows, allRowsSelected, excludedRows, columns, density, group} = useContext(DataGridStateContext);
    const {classNames} = useContext(DataGridMiscContext);

    /**
     * @type {(visibleColumns:  DataGridInternalColumn[], row: DataGridRow) => DataGridInternalCell[]}
     */
    const createCells = useCallback((visibleColumns, row) => {
        /**
         * @type {DataGridInternalCell[]}
         */
        return visibleColumns?.map((column, index, array) => {
            const firstRightPinned =
                (index === 0 && column.pinnedType === DataGridColumnPinnedTypes.right) ||
                (index > 0 && !array[index - 1].pinned && column.pinnedType === DataGridColumnPinnedTypes.right);
            const lastLeftPinned =
                ((index + 1 === array.length) && column.pinnedType === DataGridColumnPinnedTypes.left) ||
                (
                    (array.length > index + 1 && array[index + 1]?.pinned === false)
                    && column.pinnedType === DataGridColumnPinnedTypes.left
                );

            // imputing content object
            let content = row.cells[column.name];
            if (React.isValidElement(content) || typeof content !== 'object') {
                content = {
                    value: content,
                    editableValue: null,
                    editable: false
                }
            } else {
                content = {
                    ...DefaultDataGridCell,
                    ...(content ?? {}),
                }
            }

            return ({
                column: column,
                firstRightPinned: firstRightPinned,
                lastLeftPinned: lastLeftPinned,
                content: content,
                showGroupToggler: false,
            })
        }) ?? []
    }, [])

    const _visibleColumns = useMemo(() =>
        columns.filter(e =>
            e.visible &&
            e.name !== DataGridExoticColumnFields.detailedPanel
        ), [columns])

    const visibleColumns = useMemo(() => {
        return DataGridUtils.getVisibleColumnsCalculatedWidths(_visibleColumns, layoutRectWidth);
    }, [_visibleColumns, layoutRectWidth])

    /**
     * Function to recursively group rows by multiple group levels.
     * @type {(function(*, *, number=): (*))}
     */
    const recursiveGroupRows = useCallback((rowsToGroup, groupLevels, currentLevel = 0) => {
        if (currentLevel >= groupLevels.length) {
            // Base case: no more levels to group
            return rowsToGroup;
        }

        const {columnName, showOnColumn} = groupLevels[currentLevel];

        /**@type {Record<string, Array<DataGridRow>>}*/
        const rowGroups = rowsToGroup.reduce((obj, row) => {
            const groupValue = row._cellsRaw[columnName]?.groupValue;
            if (groupValue === null || typeof groupValue === "undefined") {
                // Group value does not exist
                return obj;
            }
            obj[groupValue] = [
                ...(obj[groupValue] ?? []),
                row,
            ];
            return obj;
        }, {}) ?? {};

        const rowGroupIncluded = Object.fromEntries(Object.keys(rowGroups).map(e => [e, false]));
        /**@type {DataGridInternalRow[]}*/
        const initReducedResult = [];

        return rowsToGroup.reduce((array, row) => {
            const groupValue = row._cellsRaw[columnName]?.groupValue;
            if (groupValue === null || typeof groupValue === "undefined") {
                // Group value does not exist
                array.push(row);
                return array;
            }
            if (!rowGroupIncluded[groupValue]) {
                const groupedRows = rowGroups[groupValue].filter(e => e.key !== row.key) ?? [];
                // Recursively group the groupedRows
                const nestedGroupedRows = recursiveGroupRows(groupedRows, groupLevels, currentLevel + 1);
                array.push({
                    ...row,
                    cells: row.cells.map(e => ({
                        ...e,
                        showGroupToggler: e.column.name === showOnColumn,
                        groupedRowsLength: nestedGroupedRows.length,
                    })),
                    groupedRows: nestedGroupedRows,
                });
                rowGroupIncluded[groupValue] = true;
                return array;
            }
            return array;
        }, initReducedResult);
    }, [])


    /**@type {Array<DataGridInternalRow>}*/
    const visibleRows = useMemo(() => {
        const colCount = visibleColumns?.length;
        const groups = Array.isArray(group) ? group : !group ? undefined : [group];

        /**@type {Array<DataGridInternalRow>}*/
        let result = rows?.map(row => ({
            ...row,
            selected: (allRowsSelected && !excludedRows.find(e => e.key === row.key)) ||
                (!allRowsSelected && !!selectedRows.find(e => e.key === row.key)),
            detailedPanel: row.cells[DataGridExoticColumnFields.detailedPanel],
            colCount: colCount,
            cells: createCells(visibleColumns, row),
            _cellsRaw: row.cells,
        })) ?? [];

        if (!groups?.length) {
            // no group
            return result;
        }

        result = recursiveGroupRows(result, groups);
        return result;
    }, [visibleColumns, rows, allRowsSelected, selectedRows, excludedRows, group, createCells, recursiveGroupRows]);
    

    return (
        <>
            <tbody className={classnames(
                'data-grid-body',
                classNames.body,
            )}>
            {
                visibleRows?.map(row => (
                    <DataGridBodyRow
                        density={density}
                        key={row.key}
                        row={row}
                        startScrolling={startScrolling}
                    />
                ))
            }
            </tbody>
        </>
    )
}

export default DataGridBody;
